import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createPost, getAllPosts } from '../api/postApi';
import { getCategories } from '../api/categoryApi';
import { cleanClaim } from '../utils/truthpickContent';
import { youtubeVideoId } from '../utils/videoDiscussion';
import './CreatePostPage.css';

type Category = {
    id: number;
    name: string;
};

const CreatePostPage = () => {
    const [content, setContent] = useState('');
    const [category, setCategory] = useState('');
    const [categories, setCategories] = useState<Category[]>([]);
    const [recentPosts, setRecentPosts] = useState<{ id: number; content: string; category?: string }[]>([]);
    const [isCategoryOpen, setIsCategoryOpen] = useState(false);
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const requestedCategory = new URLSearchParams(location.search).get('category');
    const requestedVideo = new URLSearchParams(location.search).get('video');
    const referenceVideoId = requestedVideo && youtubeVideoId(`https://www.youtube.com/watch?v=${requestedVideo}`);
    const referenceLine = referenceVideoId ? `참고 영상: https://www.youtube.com/watch?v=${referenceVideoId}` : '';
    const backTo = (location.state as any)?.from === '/board' ? '/board' : '/';

    useEffect(() => {
        const loadCategories = async () => {
            try {
                const data = await getCategories();
                setCategories(data);
                if(data.length > 0) setCategory(data.some((item: Category) => item.name === requestedCategory) ? requestedCategory! : data[0].name);
            } catch (requestError) {
                console.error(requestError);
                setError('주제 목록을 불러오지 못했어요.');
            }
        };

        loadCategories();
    }, [requestedCategory]);

    useEffect(() => {
        getAllPosts().then((items) => setRecentPosts(Array.isArray(items) ? [...items].sort((a, b) => b.id - a.id).slice(0, 4) : [])).catch(() => setRecentPosts([]));
    }, []);

    useEffect(() => {
        const closeOnOutsidePress = (event: MouseEvent) => {
            if(!(event.target as HTMLElement).closest('.category-select-wrap')) {
                setIsCategoryOpen(false);
            }
        };
        const closeOnEscape = (event: KeyboardEvent) => {
            if(event.key === 'Escape') {
                setIsCategoryOpen(false);
            }
        };

        document.addEventListener('mousedown', closeOnOutsidePress);
        document.addEventListener('keydown', closeOnEscape);
        return () => {
            document.removeEventListener('mousedown', closeOnOutsidePress);
            document.removeEventListener('keydown', closeOnEscape);
        };
    }, []);

    const handleSubmit = async () => {
        if(!content.replace(referenceLine, '').trim() || !category) {
            setError(referenceVideoId ? '영상 링크와 함께 확인하고 싶은 주장을 적어주세요.' : '모든 항목을 입력하세요.');
            return;
        }

        setError('');
        setIsSubmitting(true);
        try {
            await createPost(content.trim(), category);
            navigate('/board');
        } catch (requestError: any) {
            console.error(requestError);
            setError(requestError?.response?.data?.message || '게시글 등록에 실패했어요.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <main className="create-page">
            <header className="create-intro">
                <p className="create-label">주장 제보</p>
                <h1 className="create-title">무엇이 진실을 덮고 있나요?</h1>
                <p>사람들이 근거를 살피고 판단할 수 있도록 한 가지 주장에 집중해 주세요.</p>
                <div className="create-editorial">
                    <h2>좋은 질문을 만드는 법</h2>
                    <ol><li>확인하고 싶은 주장을 한 문장으로 적어보세요.</li><li>왜 궁금한지 배경이나 들은 이야기를 덧붙이세요.</li><li>다른 사람이 판단할 수 있도록 출처나 단서를 남겨주세요.</li></ol>
                </div>
                {referenceVideoId && <div className="create-editorial create-video-reference"><h2>영상에서 시작한 질문</h2><p>영상 링크를 출처로 남기고, 직접 확인하고 싶은 주장을 적어보세요.</p><a href={`https://www.youtube.com/watch?v=${referenceVideoId}`} target="_blank" rel="noopener noreferrer">참고 영상 열기 ↗</a><button type="button" disabled={content.includes(referenceLine)} onClick={() => setContent((current) => `${current.trimEnd()}${current.trim() ? '\n\n' : ''}${referenceLine}`)}>본문에 영상 링크 넣기</button></div>}
                {recentPosts.length > 0 && <div className="create-editorial create-recent"><h2>최근 올라온 질문</h2><ul>{recentPosts.map((post) => <li key={post.id}><a href={`#/board?post=${post.id}`}><span>{post.category || '이야기'}</span>{cleanClaim(post.content, 54)}</a></li>)}</ul><a className="create-more-link" href="#/board">다른 이야기 살펴보기 ↗</a></div>}
            </header>

            <form
                className="create-card"
                onSubmit={(event) => {
                    event.preventDefault();
                    handleSubmit();
                }}
            >
                <section className="category-section" aria-labelledby="category-heading">
                    <div>
                        <h2 id="category-heading" className="category-label">주제</h2>
                        <p className="category-description">가장 가까운 주제를 선택하세요.</p>
                    </div>

                    <div className="category-select-wrap">
                        <button
                            type="button"
                            className="category-select"
                            aria-haspopup="listbox"
                            aria-expanded={isCategoryOpen}
                            onClick={() => setIsCategoryOpen((prev) => !prev)}
                        >
                            <span className={category ? '' : 'category-placeholder'}>{category || '카테고리 선택'}</span>
                            <span className={`category-arrow ${isCategoryOpen ? 'open' : ''}`} aria-hidden="true">⌄</span>
                        </button>

                        {isCategoryOpen && (
                            <div className="category-dropdown" role="listbox" aria-label="주제 선택">
                                {categories.map((item) => (
                                    <button
                                        type="button"
                                        key={item.id}
                                        role="option"
                                        aria-selected={category === item.name}
                                        className={`category-option ${category === item.name ? 'selected' : ''}`}
                                        onClick={() => {
                                            setCategory(item.name);
                                            setIsCategoryOpen(false);
                                        }}
                                    >
                                        <span className="category-option-label">{item.name}</span>
                                        {category === item.name && <span className="category-check" aria-hidden="true">✓</span>}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </section>

                <div className="create-field">
                    <div className="create-field-heading">
                        <label className="field-label" htmlFor="truth-content">질문 내용</label>
                        <span>{content.length}자</span>
                    </div>
                    <textarea
                        id="truth-content"
                        placeholder="예: 달은 매년 지구에서 조금씩 멀어지고 있다."
                        value={content}
                        onChange={(event) => setContent(event.target.value)}
                        className="create-textarea"
                        aria-describedby="truth-content-help"
                    />
                    <p id="truth-content-help" className="field-help">사실 여부를 판단할 수 있는 문장으로 구체적으로 작성해 주세요.</p>
                </div>

                {error && <p className="create-error" role="alert">{error}</p>}

                <div className="create-actions">
                    <button type="button" className="create-secondary-button" onClick={() => navigate(backTo)}>취소</button>
                    <button type="submit" className="create-button" disabled={isSubmitting}>
                        {isSubmitting ? '등록 중…' : '진실 올리기'}
                    </button>
                </div>
            </form>
        </main>
    );
};

export default CreatePostPage;
